import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useHub } from "../lib/hubContent";
import { AddItemButton, EditItemButton, EditSectionButton } from "./HubEditor";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";

export default function CultureIdeas() {
  const hub = useHub();
  const c = hub.section("ideas_head");
  const articles = hub.collection("ideas") || [];
  const poems = (hub.collection("poems") || []).map((p) => ({
    id: p.id,
    category: "Poetry",
    color: "#C026D3",
    title: p.title,
    dek: (p.body || "").split("\n").filter(Boolean).slice(0, 2).join(" "),
    issue: "The quiet pages",
    read: "1 min",
    poem: true,
    raw: p,
  }));
  const entries = [...articles, ...poems];
  const [filter, setFilter] = useState("All");
  const categories = ["All", ...new Set(entries.map((a) => a.category))];
  const visible = filter === "All" ? entries : entries.filter((a) => a.category === filter);

  return (
    <section id="ideas" data-testid="ideas-section" className="relative bg-[#EAF0F5] text-[#1E3A8A] py-24 md:py-32 overflow-hidden">
      <EditSectionButton sectionKey="ideas_head" label="Edit copy" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow={c.eyebrow}
          title={<>{c.title_a} <span className="italic text-en-blue">+</span> {c.title_b}</>}
          note={c.note}
          noteColor="text-en-orange"
        />

        <Reveal className="mb-12">
          <div className="flex flex-wrap items-center gap-2.5" role="group" aria-label="Filter articles">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`ideas-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(cat)}
                aria-pressed={filter === cat}
                className={`rounded-full px-5 py-2 font-mono text-[10px] tracking-[0.2em] uppercase border transition-all ${
                  filter === cat
                    ? "bg-en-blue text-white border-en-blue"
                    : "border-blue-200 bg-white/60 text-blue-900/60 hover:border-en-blue hover:text-en-blue"
                }`}
              >
                {cat}
              </button>
            ))}
            <AddItemButton kind="ideas" label="New idea" className="text-blue-900/70" />
          </div>
        </Reveal>

        <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((a) => (
              <motion.article
                layout
                key={a.id}
                data-testid={`article-card-${a.id}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="group relative rounded-2xl bg-white border border-blue-100 p-7 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 cursor-pointer flex flex-col"
                onClick={() => a.poem && document.querySelector("#poetry")?.scrollIntoView({ behavior: "smooth" })}
              >
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <EditItemButton kind={a.poem ? "poems" : "ideas"} item={a.poem ? a.raw : a} />
                </div>
                <div className="flex items-center justify-between mb-5">
                  <span className="rounded-full px-3 py-1 font-mono text-[9px] tracking-[0.2em] uppercase text-white" style={{ backgroundColor: a.color }}>
                    {a.category}
                  </span>
                  <span className="font-mono text-[10px] text-blue-900/40">{a.poem ? a.issue : `Issue ${a.issue}`}</span>
                </div>
                <h3 className="font-display font-bold text-2xl leading-tight mb-3 text-slate-900 group-hover:text-en-blue transition-colors">
                  {a.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600 flex-1">{a.dek}</p>
                <div className="flex items-center justify-between mt-7 pt-5 border-t border-blue-50">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-blue-900/40">{a.read} read</span>
                  <ArrowUpRight size={16} className="text-blue-900/30 group-hover:text-en-blue group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal delay={0.15}>
          <p className="mt-12 font-hand text-2xl text-blue-900/50 rotate-[-1deg]">{c.footer_note}</p>
        </Reveal>
      </div>
    </section>
  );
}
